<template>
  <div class="family-patients family-page bg-slate-50">
    <header
      class="family-page-header bg-white/90 backdrop-blur-md border-b border-slate-100"
      :style="{ paddingTop: 'env(safe-area-inset-top)' }"
    >
      <div class="px-4 py-3 flex items-center justify-between gap-3">
        <div class="flex items-center gap-3 min-w-0">
          <button
            type="button"
            class="w-9 h-9 rounded-full bg-slate-50 flex items-center justify-center text-slate-500"
            @click="goBack"
          >
            <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
              <path stroke-linecap="round" stroke-linejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <h1 class="text-base font-bold text-slate-900 truncate">陪护人档案</h1>
        </div>
        <button
          type="button"
          class="flex-shrink-0 px-3 py-1.5 rounded-full bg-family-blue text-white text-xs font-semibold"
          @click="openEditor()"
        >
          新增
        </button>
      </div>
    </header>

    <main class="family-page-body px-4 py-4 pb-8">
      <div v-if="loading" class="py-16 text-center text-sm text-slate-400">加载中...</div>
      <div v-else-if="patients.length === 0" class="py-16 text-center">
        <p class="text-sm text-slate-400">暂无陪护人档案</p>
        <p class="text-xs text-slate-300 mt-2">添加后可在一键发起陪护时快速选择</p>
      </div>

      <article
        v-for="item in patients"
        :key="item.id"
        class="bg-white rounded-xl p-4 mb-3 shadow-[0_2px_12px_rgb(0,0,0,0.04)]"
      >
        <div class="flex items-start justify-between gap-2">
          <div class="min-w-0">
            <div class="flex items-center gap-2">
              <h3 class="text-base font-bold text-slate-900">{{ item.name }}</h3>
              <span v-if="item.isDefault" class="px-2 py-0.5 rounded text-[10px] bg-emerald-50 text-emerald-600">默认</span>
            </div>
            <p class="text-xs text-slate-500 mt-1">
              {{ item.genderLabel || "—" }} · {{ item.age != null ? item.age + "岁" : "—" }} · {{ item.careLevelLabel || "—" }}
            </p>
            <p v-if="item.relation" class="text-xs text-slate-400 mt-1">关系：{{ item.relation }}</p>
            <p v-if="item.conditionNote" class="text-xs text-slate-400 mt-1 line-clamp-2">{{ item.conditionNote }}</p>
          </div>
          <div class="flex flex-col gap-2 flex-shrink-0">
            <button type="button" class="text-xs text-family-blue" @click="openEditor(item)">编辑</button>
            <button type="button" class="text-xs text-red-400" @click="removeItem(item)">删除</button>
          </div>
        </div>
      </article>
    </main>

    <a-modal
      v-model="editorVisible"
      :title="editorForm.id ? '编辑档案' : '新增档案'"
      ok-text="保存"
      cancel-text="取消"
      :confirm-loading="saving"
      @ok="saveItem"
    >
      <div class="space-y-3">
        <label class="block">
          <span class="text-sm text-slate-600">姓名 <span class="text-red-500">*</span></span>
          <a-input v-model="editorForm.name" placeholder="患者姓名" />
        </label>
        <div class="flex gap-3">
          <label class="flex-1 block">
            <span class="text-sm text-slate-600">年龄</span>
            <a-input-number v-model="editorForm.age" :min="0" :max="120" class="w-full" />
          </label>
          <label class="flex-1 block">
            <span class="text-sm text-slate-600">关系</span>
            <a-input v-model="editorForm.relation" placeholder="如：父亲" />
          </label>
        </div>
        <div>
          <span class="text-sm text-slate-600">性别</span>
          <a-radio-group v-model="editorForm.gender" class="mt-1">
            <a-radio value="male">男</a-radio>
            <a-radio value="female">女</a-radio>
          </a-radio-group>
        </div>
        <div>
          <span class="text-sm text-slate-600">自理能力</span>
          <a-radio-group v-model="editorForm.careLevel" class="mt-1">
            <a-radio value="self">能自理</a-radio>
            <a-radio value="full">不能自理</a-radio>
          </a-radio-group>
        </div>
        <label class="block">
          <span class="text-sm text-slate-600">病情/备注</span>
          <a-textarea v-model="editorForm.conditionNote" :rows="3" placeholder="可选" />
        </label>
        <a-checkbox v-model="editorForm.isDefault">设为默认档案</a-checkbox>
      </div>
    </a-modal>
  </div>
</template>

<script>
import { familyPatientApi } from "./familyPatientApi";
import { familyGoBack } from "./familyNavigation";

const emptyForm = () => ({
  id: null,
  name: "",
  age: null,
  gender: "male",
  careLevel: "self",
  relation: "",
  conditionNote: "",
  isDefault: false,
});

export default {
  name: "FamilyPatientArchive",
  data() {
    return {
      loading: false,
      saving: false,
      patients: [],
      editorVisible: false,
      editorForm: emptyForm(),
    };
  },
  mounted() {
    document.title = "陪护人档案 - 安心陪护";
    document.documentElement.classList.add("family-route");
    document.body.classList.add("family-route");
    this.loadList();
  },
  beforeDestroy() {
    document.documentElement.classList.remove("family-route");
    document.body.classList.remove("family-route");
  },
  methods: {
    goBack() {
      familyGoBack(this.$router, this.$route, "profile");
    },
    async loadList() {
      this.loading = true;
      try {
        this.patients = await familyPatientApi.list();
      } catch (e) {
        this.$message.error((e && e.message) || "加载失败");
        this.patients = [];
      } finally {
        this.loading = false;
      }
    },
    openEditor(item) {
      if (item) {
        this.editorForm = {
          id: item.id,
          name: item.name,
          age: item.age,
          gender: item.gender || "male",
          careLevel: item.careLevel === "half" ? "full" : (item.careLevel || "self"),
          relation: item.relation || "",
          conditionNote: item.conditionNote || "",
          isDefault: !!item.isDefault,
        };
      } else {
        this.editorForm = emptyForm();
      }
      this.editorVisible = true;
    },
    async saveItem() {
      if (!this.editorForm.name || !this.editorForm.name.trim()) {
        this.$message.warning("请填写姓名");
        return Promise.reject();
      }
      this.saving = true;
      try {
        await familyPatientApi.save({
          id: this.editorForm.id,
          name: this.editorForm.name.trim(),
          age: this.editorForm.age,
          gender: this.editorForm.gender,
          careLevel: this.editorForm.careLevel,
          relation: this.editorForm.relation,
          conditionNote: this.editorForm.conditionNote,
          isDefault: this.editorForm.isDefault,
        });
        this.editorVisible = false;
        this.$message.success("保存成功");
        await this.loadList();
      } catch (e) {
        this.$message.error((e && e.message) || "保存失败");
      } finally {
        this.saving = false;
      }
    },
    removeItem(item) {
      this.$confirm({
        title: "删除档案",
        content: `确定删除「${item.name}」的陪护人档案吗？`,
        okText: "删除",
        okType: "danger",
        cancelText: "取消",
        onOk: async () => {
          await familyPatientApi.remove(item.id);
          this.$message.success("已删除");
          await this.loadList();
        },
      });
    },
  },
};
</script>
